import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { LanguageProvider } from "./i18n/LanguageContext";
import { AuthProvider } from "./lib/AuthContext";
import { captureRefFromUrl } from "./lib/referrals";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import CircleOfLifeLanding from "./pages/CircleOfLifeLanding";
import SurvivalOfTheFittestLanding from "./pages/SurvivalOfTheFittestLanding";
import Quiz from "./pages/Quiz";
import Lebensrad from "./pages/Lebensrad";
import EcosystemPage from "./pages/EcosystemPage";
import SubBrand from "./pages/SubBrand";
import StartHere from "./pages/StartHere";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import Academy from "./pages/Academy";
import AcademyWelcome from "./pages/AcademyWelcome";
import CourseDetail from "./pages/CourseDetail";
import CourseLearn from "./pages/CourseLearn";
import StudentDashboard from "./pages/StudentDashboard";
import AuthForm from "./pages/AuthForm";
import MediaPage from "./pages/MediaPage";
import { ForumList, ForumThread } from "./pages/Forum";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import ReferralsPage from "./pages/ReferralsPage";
import MentorPage from "./pages/MentorPage";
import AIChatPage from "./pages/AIChatPage";
import SATAppPage from "./pages/SATAppPage";
import { EventsList, EventDetail } from "./pages/Events";
import BroadcastPage from "./pages/BroadcastPage";
import { ProgrammesIndex, ProgrammeDetail } from "./pages/Programmes";
import LeadMagnet30 from "./pages/LeadMagnet30";
import EventsPipeline from "./pages/EventsPipeline";
import CheckoutRecovery from "./pages/CheckoutRecovery";
import LOSLanding from "./pages/LOSLanding";
import LOSScan from "./pages/LOSScan";
import SatBotAdmin from "./pages/SatBotAdmin";
import PartnerProjektePage from "./pages/PartnerProjektePage";
import PartnerWall from "./pages/PartnerWall";
import Herzbewegung from "./pages/Herzbewegung";
import SatMaster from "./pages/SatMaster";
import SatHtc from "./pages/SatHtc";
import Erstgespraech from "./pages/Erstgespraech";
import StimmenSammlung from "./pages/StimmenSammlung";
import AdminLeads from "./pages/AdminLeads";
import NenadProfile from "./pages/NenadProfile";
import HumanPotentialAnalysis from "./pages/HumanPotentialAnalysis";
import HpaHome from "./pages/hpa/HpaHome";
import HpaIntro from "./pages/hpa/HpaIntro";
import HpaConsent from "./pages/hpa/HpaConsent";
import HpaAssessment from "./pages/hpa/HpaAssessment";
import HpaPaused from "./pages/hpa/HpaPaused";
import HpaReviewPending from "./pages/hpa/HpaReviewPending";
import HpaProfile from "./pages/hpa/HpaProfile";
import { HpaMentorList, HpaMentorDetail } from "./pages/hpa/HpaMentor";
import AuraJourney from "./pages/aura/AuraJourney";
import AuraMentorCockpit from "./pages/mentor/aura/AuraMentorCockpit";
import HtcJourney from "./pages/htc/HtcJourney";
import HtcMentorCockpit from "./pages/mentor/htc/HtcMentorCockpit";
import CircleAssessment from "./pages/circle/CircleAssessment";
import SurvivalAssessment from "./pages/survival/SurvivalAssessment";
import TransformationMastery from "./pages/mastery/TransformationMastery";
import { RequireHpaPreviewAccess } from "./components/auth/HpaGuard";
import SatMasterDeck from "./pages/SatMasterDeck";
import SatMentorDeck from "./pages/SatMentorDeck";
import SatVaterstimmeDeck from "./pages/SatVaterstimmeDeck";
import SatMutterstimmeDeck from "./pages/SatMutterstimmeDeck";
import SatMaennerstimmeDeck from "./pages/SatMaennerstimmeDeck";
import SatFrauenstimmeDeck from "./pages/SatFrauenstimmeDeck";
import SatKinderstimmeDeck from "./pages/SatKinderstimmeDeck";
import SatElternstimmeDeck from "./pages/SatElternstimmeDeck";
import SatHerzbewegungDeck from "./pages/SatHerzbewegungDeck";
import Herzbotschafter from "./pages/Herzbotschafter";
import HerzbotschafterAdmin from "./pages/HerzbotschafterAdmin";
import { Impressum, Datenschutz, AGB, AboutSAT } from "./pages/LegalPages";
import DecksLibrary from "./pages/DecksLibrary";
import SiteSettings from "./pages/SiteSettings";
import { CourseEditor, DripEditor } from "./pages/AdminEditors";
import PremiumPlayer from "./pages/v2/PremiumPlayer";
import V2CourseState from "./pages/v2/V2CourseState";
import { V2CoursesList, V2CourseBuilder } from "./pages/v2/V2CourseBuilder";
import PreviewAsLearner from "./pages/v2/PreviewAsLearner";
import MentorReviewQueue from "./pages/v2/MentorReviewQueue";
import MentorMySubmissions from "./pages/v2/MentorMySubmissions";
import SocietyBarometerPage from "./pages/SocietyBarometerPage";
import AdminStatistics from "./pages/AdminStatistics";
import TestimonialsAdmin from "./pages/TestimonialsAdmin";
import SatMasterSalespage from "./pages/SatMasterSalespage";
import SatMentorSalespage from "./pages/SatMentorSalespage";
import ApplicationForm from "./pages/ApplicationForm";
import Shop from "./pages/Shop";
import VoicesIndex from "./pages/VoicesIndex";
import VoiceSalesPage from "./pages/VoiceSalesPage";
import AdminVoiceInquiries from "./pages/AdminVoiceInquiries";
import MagicHummingbird from "./components/MagicHummingbird";
import MagicButterfly from "./components/MagicButterfly";
import SatBotWidget from "./components/SatBotWidget";
import SATIntroGate from "./components/cinematic/SATIntroGate";
import { RequireAuth, RequireRole } from "./components/auth/Guards";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!window.location.hash) window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const RefCapture = () => {
  useEffect(() => { captureRefFromUrl(); }, []);
  return null;
};

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App relative">
            <SATIntroGate />
            <Nav />
            <ScrollToTop />
            <RefCapture />
            <main className="relative z-10">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/self-discovery/circle-of-life" element={<CircleOfLifeLanding />} />
                <Route path="/self-discovery/survival-of-the-fittest" element={<SurvivalOfTheFittestLanding />} />
                <Route path="/start" element={<StartHere />} />
                <Route path="/quiz" element={<Quiz />} />
                <Route path="/lebensrad" element={<Lebensrad />} />
                <Route path="/ecosystem" element={<EcosystemPage />} />
                <Route path="/ecosystem/:key" element={<SubBrand />} />

                <Route path="/academy" element={<Academy />} />
                <Route path="/academy/dashboard" element={<RequireAuth><StudentDashboard /></RequireAuth>} />
                {/* Block 1 (Mastermatrix v1.1) · Willkommensraum nach Aktivierung */}
                <Route path="/academy/welcome" element={<RequireAuth><AcademyWelcome /></RequireAuth>} />
                <Route path="/akademie/decks" element={<DecksLibrary />} />
                <Route path="/academy/:slug" element={<CourseDetail />} />
                <Route path="/academy/:slug/learn" element={<RequireAuth><CourseLearn /></RequireAuth>} />
                {/* Block 3 · V2 Course Engine (Premium Player) */}
                <Route path="/academy/:courseSlug/learn-v2/:lessonSlug" element={<RequireAuth><PremiumPlayer /></RequireAuth>} />
                <Route path="/academy/v2/:courseSlug" element={<RequireAuth><V2CourseState /></RequireAuth>} />

                <Route path="/podcast" element={<MediaPage kind="podcast" />} />
                <Route path="/tv" element={<MediaPage kind="tv" />} />

                <Route path="/forum" element={<ForumList />} />
                <Route path="/forum/:id" element={<ForumThread />} />

                <Route path="/referrals" element={<ReferralsPage />} />
                {/* Block 6 · Kanonische Salespages */}
                <Route path="/sat-master" element={<SatMasterSalespage />} />
                <Route path="/sat-mentor" element={<SatMentorSalespage />} />
                <Route path="/sat-master/bewerbung" element={<ApplicationForm programme="SAT_MASTER" />} />
                <Route path="/sat-mentor/bewerbung" element={<ApplicationForm programme="SAT_MENTOR" />} />
                {/* Legacy Mentor · Canonical zeigt auf /sat-mentor */}
                <Route path="/mentor" element={<MentorPage />} />
                <Route path="/mentor/deck" element={<SatMentorDeck />} />
                <Route path="/vaterstimme/deck" element={<SatVaterstimmeDeck />} />
                <Route path="/mutterstimme/deck" element={<SatMutterstimmeDeck />} />
                <Route path="/maennerstimme/deck" element={<SatMaennerstimmeDeck />} />
                <Route path="/frauenstimme/deck" element={<SatFrauenstimmeDeck />} />
                <Route path="/kinderstimme/deck" element={<SatKinderstimmeDeck />} />
                <Route path="/elternstimme/deck" element={<SatElternstimmeDeck />} />
                <Route path="/herzbewegung/deck" element={<SatHerzbewegungDeck />} />
                <Route path="/herzbewegung/herzbotschafter" element={<Herzbotschafter />} />
                <Route path="/ai" element={<AIChatPage />} />
                <Route path="/satapp" element={<SATAppPage />} />

                <Route path="/events" element={<EventsList />} />
                <Route path="/events/:slug" element={<EventDetail />} />

                <Route path="/programme" element={<ProgrammesIndex />} />
                <Route path="/programme/:slug" element={<ProgrammeDetail />} />
                <Route path="/30-spielregeln" element={<LeadMagnet30 />} />
                <Route path="/los" element={<LOSLanding />} />
                <Route path="/los/scan" element={<LOSScan />} />
                <Route path="/sat-master" element={<SatMaster />} />
                <Route path="/sat-master/deck" element={<SatMasterDeck />} />
                <Route path="/sat-master/:slug" element={<SatMaster />} />
                <Route path="/sat-htc" element={<SatHtc />} />
                <Route path="/sat-htc-human-transformation-code" element={<SatHtc />} />
                <Route path="/methoden/human-transformation-code" element={<SatHtc />} />
                <Route path="/methoden/human-potential-analysis" element={<HumanPotentialAnalysis />} />
                {/* Block 11.1 · authentifizierte HPA-Journey (Preview only, fail-closed) */}
                <Route path="/hpa" element={<RequireHpaPreviewAccess><HpaHome /></RequireHpaPreviewAccess>} />
                <Route path="/hpa/intro" element={<RequireHpaPreviewAccess><HpaIntro /></RequireHpaPreviewAccess>} />
                <Route path="/hpa/consent" element={<RequireHpaPreviewAccess><HpaConsent /></RequireHpaPreviewAccess>} />
                <Route path="/hpa/assessment" element={<RequireHpaPreviewAccess><HpaAssessment /></RequireHpaPreviewAccess>} />
                <Route path="/hpa/paused" element={<RequireHpaPreviewAccess><HpaPaused /></RequireHpaPreviewAccess>} />
                <Route path="/hpa/review-pending" element={<RequireHpaPreviewAccess><HpaReviewPending /></RequireHpaPreviewAccess>} />
                <Route path="/hpa/profile" element={<RequireHpaPreviewAccess><HpaProfile /></RequireHpaPreviewAccess>} />
                <Route path="/hpa/profile/:profileId" element={<RequireHpaPreviewAccess><HpaProfile /></RequireHpaPreviewAccess>} />
                {/* Block 11.4 · Mentor Cockpit — nur MENTOR_REVIEWER + Admin-Rollen */}
                <Route path="/mentor/hpa" element={
                  <RequireHpaPreviewAccess>
                    <RequireRole roles={["MENTOR_REVIEWER", "SUPER_ADMIN", "ACADEMY_ADMIN"]}>
                      <HpaMentorList />
                    </RequireRole>
                  </RequireHpaPreviewAccess>
                } />
                <Route path="/mentor/hpa/:profileId" element={
                  <RequireHpaPreviewAccess>
                    <RequireRole roles={["MENTOR_REVIEWER", "SUPER_ADMIN", "ACADEMY_ADMIN"]}>
                      <HpaMentorDetail />
                    </RequireRole>
                  </RequireHpaPreviewAccess>
                } />
                {/* AURA M3 · Internal Preview */}
                <Route path="/aura" element={<AuraJourney />} />
                <Route path="/mentor/aura" element={
                  <RequireRole roles={["MENTOR_REVIEWER", "SUPER_ADMIN", "ACADEMY_ADMIN"]}>
                    <AuraMentorCockpit />
                  </RequireRole>
                } />
                {/* HTC M3 · Internal Preview */}
                <Route path="/htc" element={<HtcJourney />} />
                <Route path="/mentor/htc" element={
                  <RequireRole roles={["MENTOR_REVIEWER", "SUPER_ADMIN", "ACADEMY_ADMIN"]}>
                    <HtcMentorCockpit />
                  </RequireRole>
                } />
                {/* Circle of Life · STEP 1 · Internal Preview */}
                <Route path="/circle" element={<CircleAssessment />} />
                {/* Survival of the Fittest · STEP 2 · Internal Preview */}
                <Route path="/survival" element={<SurvivalAssessment />} />
                {/* Transformation Mastery · STEP 3 · Internal Preview */}
                <Route path="/mastery" element={<TransformationMastery />} />
                <Route path="/nenad-vladovic" element={<NenadProfile />} />
                <Route path="/erstgespraech" element={<Erstgespraech />} />
                <Route path="/stimmen-decks" element={<StimmenSammlung />} />

                <Route path="/auth/login" element={<AuthForm mode="login" />} />
                <Route path="/auth/register" element={<AuthForm mode="register" />} />

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/leads" element={<AdminLeads />} />
                <Route path="/admin/courses" element={<CourseEditor />} />
                <Route path="/admin/drips" element={<DripEditor />} />
                <Route path="/admin/broadcast" element={<BroadcastPage />} />
                <Route path="/admin/testimonials" element={<TestimonialsAdmin />} />
                <Route path="/admin/events" element={<EventsPipeline />} />
                <Route path="/admin/checkout-recovery" element={<CheckoutRecovery />} />
                <Route path="/admin/satbot" element={<SatBotAdmin />} />
                <Route path="/admin/settings" element={<SiteSettings />} />
                <Route path="/admin/herzbotschafter" element={<HerzbotschafterAdmin />} />
                {/* Block 3 · V2 Admin Builder + Preview */}
                <Route path="/admin/v2/courses" element={<RequireRole roles={["SUPER_ADMIN","ACADEMY_ADMIN","CONTENT_EDITOR"]}><V2CoursesList /></RequireRole>} />
                <Route path="/admin/v2/courses/:courseId" element={<RequireRole roles={["SUPER_ADMIN","ACADEMY_ADMIN","CONTENT_EDITOR"]}><V2CourseBuilder /></RequireRole>} />
                <Route path="/admin/v2/preview-as-learner/:courseSlug" element={<RequireRole roles={["SUPER_ADMIN","ACADEMY_ADMIN"]}><PreviewAsLearner /></RequireRole>} />
                {/* Block 5 · SAT MENTOR Reviewer + Learner Submissions */}
                <Route path="/admin/mentor-reviews" element={<RequireRole roles={["SUPER_ADMIN","ACADEMY_ADMIN","MENTOR_REVIEWER"]}><MentorReviewQueue /></RequireRole>} />
                <Route path="/academy/mentor/submissions" element={<RequireAuth><MentorMySubmissions /></RequireAuth>} />
                {/* DACH-Gesellschaftsbarometer */}
                <Route path="/gesellschaftsbarometer" element={<SocietyBarometerPage />} />
                <Route path="/admin/statistics" element={<RequireRole roles={["SUPER_ADMIN","ACADEMY_ADMIN"]}><AdminStatistics /></RequireRole>} />
                <Route path="/about" element={<AboutSAT />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/agb" element={<AGB />} />
                <Route path="/partner-projekte" element={<PartnerProjektePage />} />
                <Route path="/partner-wall" element={<PartnerWall />} />
                <Route path="/herzbewegung" element={<Herzbewegung />} />

                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/stimmen" element={<VoicesIndex />} />
                <Route path="/stimmen/:slug" element={<VoiceSalesPage />} />
                <Route path="/admin/voice-inquiries" element={<AdminVoiceInquiries />} />

                <Route path="*" element={<Home />} />
              </Routes>
            </main>
            <Footer />
            <MagicHummingbird />
            <MagicButterfly />
            <SatBotWidget />
            <Toaster theme="dark" position="bottom-right" />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
